import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { 
  Building2, Search, Loader2, ExternalLink, ToggleLeft, ToggleRight, Edit, UserCog, ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiGet, apiPost, apiPut } from '../api';
import { formatApiError } from '../lib/apiError';

export const AdminStudiosPage = () => {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    slug: '',
    description: '',
    address: '',
    city: '',
    county: '',
    eircode: '',
    email: '',
    phone: '',
    category: 'fitness',
    company_type: 'sole_trader',
    cancellation_notice_hours: 24,
    cancellation_refund_type: 'class_pass_credit',
    late_cancel_penalty: 0,
    is_active: true,
    platform_fee_percentage_override: ''  // empty string => unset (use plan/global default)
  });
  const [editLoading, setEditLoading] = useState(false);

  // Helper function to sanitize slug - handles URLs, spaces, and special characters
  const sanitizeSlug = (input) => {
    if (!input) return '';
    let slug = input;
    
    // If it looks like a URL, try to extract just the path/slug part
    if (slug.includes('://') || slug.includes('.ie') || slug.includes('.com')) {
      // Remove protocol
      slug = slug.replace(/^https?:\/\//, '');
      // Remove domain
      slug = slug.replace(/^[^/]+\//, '');
      // Remove trailing slashes
      slug = slug.replace(/\/+$/, '');
      // If there's still a path, get the last segment
      if (slug.includes('/')) {
        slug = slug.split('/').pop();
      }
    }
    
    // Convert to lowercase, replace spaces with hyphens, remove invalid chars
    slug = slug.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')  // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    return slug;
  };

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      const data = await apiGet('/api/admin/studios');
      setStudios(data || []);
    } catch (e) {
      toast.error('Failed to load studios');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (studioId, currentStatus) => {
    setTogglingId(studioId);
    try {
      await apiPut('/api/studios/{studio_id}', {
        params: { studio_id: studioId },
        body: { is_active: !currentStatus },
      });
      toast.success(`Studio ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchStudios();
    } catch (e) {
      toast.error('Failed to update studio status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenEdit = (studio) => {
    setSelectedStudio(studio);
    setEditForm({
      name: studio.name || '',
      slug: studio.slug || '',
      description: studio.description || '',
      address: studio.address || '',
      city: studio.city || '',
      county: studio.county || '',
      eircode: studio.eircode || '',
      email: studio.email || '',
      phone: studio.phone || '',
      category: studio.category || 'fitness',
      company_type: studio.company_type || 'sole_trader',
      cancellation_notice_hours: studio.cancellation_notice_hours || 24,
      cancellation_refund_type: studio.cancellation_refund_type || 'class_pass_credit',
      late_cancel_penalty: studio.late_cancel_penalty || 0,
      is_active: studio.is_active !== false,
      platform_fee_percentage_override:
        studio.platform_fee_percentage_override === null || studio.platform_fee_percentage_override === undefined
          ? ''
          : (studio.platform_fee_percentage_override * 100).toString()  // convert decimal -> percent for display
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedStudio) return;
    
    const cleanSlug = sanitizeSlug(editForm.slug);
    if (!cleanSlug) {
      toast.error('Please enter a valid URL slug (letters, numbers, and hyphens only)');
      return;
    }
    
    setEditLoading(true);
    try {
      // Convert percent input back to decimal for backend (0.75% -> 0.0075).
      // Empty string means "unset override" -> send null so backend $set will clear it.
      let feeOverride = null;
      if (editForm.platform_fee_percentage_override !== '' && editForm.platform_fee_percentage_override !== null && editForm.platform_fee_percentage_override !== undefined) {
        const pct = parseFloat(editForm.platform_fee_percentage_override);
        if (Number.isFinite(pct) && pct >= 0 && pct <= 100) {
          feeOverride = pct / 100;
        } else {
          toast.error('Platform fee override must be between 0 and 100 (%)');
          setEditLoading(false);
          return;
        }
      }

      await apiPut('/api/studios/{studio_id}', {
        params: { studio_id: selectedStudio.id },
        body: {
          name: editForm.name,
          slug: cleanSlug,
          description: editForm.description,
          address: editForm.address,
          city: editForm.city,
          county: editForm.county,
          eircode: editForm.eircode,
          email: editForm.email,
          phone: editForm.phone,
          category: editForm.category,
          company_type: editForm.company_type,
          cancellation_notice_hours: parseInt(editForm.cancellation_notice_hours) || 24,
          cancellation_refund_type: editForm.cancellation_refund_type,
          late_cancel_penalty: parseFloat(editForm.late_cancel_penalty) || 0,
          is_active: editForm.is_active,
          platform_fee_percentage_override: feeOverride,
        },
      });
      toast.success('Studio updated successfully');
      setShowEditDialog(false);
      fetchStudios();
    } catch (err) {
      const detail = formatApiError(err, '');
      toast.error(detail || 'Failed to update studio');
    } finally {
      setEditLoading(false);
    }
  };

  const handleImpersonate = async (studio) => {
    if (!window.confirm(`Are you sure you want to impersonate the admin of "${studio.name}"?\n\nYou will be logged in as the studio owner and redirected to their dashboard.`)) {
      return;
    }
    
    setImpersonatingId(studio.id);
    try {
      const data = await apiPost('/api/admin/impersonate/{studio_id}', {
        params: { studio_id: studio.id },
      });

      // Iter-46an: backend has already issued ss_access/ss_refresh/ss_csrf
      // cookies for the studio owner and an ss_impersonator cookie holding
      // the super-admin's id. Just store the banner metadata for display
      // and re-sync the auth context from the cookies.
      sessionStorage.setItem('impersonation', JSON.stringify({
        active: true,
        impersonatedBy: data.impersonated_by,
        studioName: data.studio.name,
      }));

      // Drop any stale Bearer token; the cookie is now the source of truth.
      localStorage.removeItem('token');
      await refreshUser();

      toast.success(`Now impersonating ${studio.name}'s admin`);
      navigate('/studio');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to impersonate studio admin'));
    } finally {
      setImpersonatingId(null);
    }
  };

  // Iter-46ah: Super admin can wipe a studio owner's MFA enrollment.
  const handleResetMfa = async (studio) => {
    if (!studio.owner_id) {
      toast.error('No owner found for this studio');
      return;
    }
    if (!window.confirm(`Reset MFA for ${studio.name}'s owner?\n\nThis clears their authenticator app pairing and all backup codes. The owner will have 5 logins to re-enroll.`)) {
      return;
    }
    try {
      const data = await apiPost('/api/admin/users/{user_id}/reset-mfa', {
        params: { user_id: studio.owner_id },
      });
      toast.success(data.message || 'MFA reset');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to reset MFA'));
    }
  };

  const filteredStudios = studios.filter(studio => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      studio.name?.toLowerCase().includes(term) ||
      studio.slug?.toLowerCase().includes(term) ||
      studio.email?.toLowerCase().includes(term)
    );
  });

  const activeCount = studios.filter(s => s.is_active).length;
  const inactiveCount = studios.filter(s => !s.is_active).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-studios-page">
        <div>
          <h1 className="text-3xl font-bold">All Studios</h1>
          <p className="text-muted-foreground">Manage all registered studios on the platform</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{studios.length}</p>
              <p className="text-sm text-muted-foreground">Total Studios</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{activeCount}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{inactiveCount}</p>
              <p className="text-sm text-muted-foreground">Inactive</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name, slug, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Studios List */}
        <Card>
          <CardHeader>
            <CardTitle>Studios ({filteredStudios.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredStudios.length > 0 ? (
              <div className="space-y-4">
                {filteredStudios.map((studio, i) => (
                  <div 
                    key={studio.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`studio-row-${i}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden">
                        {studio.logo_url ? (
                          <img 
                            src={studio.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${studio.logo_url}` : studio.logo_url}
                            alt={studio.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Building2 className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div>
                        {/* Bug R17 fix (Iter-67): inline the studio URL with
                            the name so they read as a single visual unit
                            (previously they looked disconnected across two
                            unrelated lines). Wraps naturally on mobile. */}
                        <p className="font-medium leading-tight" data-testid={`admin-studio-name-${studio.slug || studio.id}`}>
                          {studio.name}
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            · studioscheduler.ie/{studio.slug}
                          </span>
                        </p>
                        {studio.email && (
                          <p className="text-xs text-muted-foreground mt-0.5">{studio.email}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge variant={studio.is_active ? 'default' : 'secondary'}>
                          {studio.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          Created {format(new Date(studio.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleImpersonate(studio)}
                          disabled={impersonatingId === studio.id}
                          title="Impersonate studio admin"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        >
                          {impersonatingId === studio.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserCog className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResetMfa(studio)}
                          title="Reset MFA for studio owner"
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          data-testid={`reset-mfa-${studio.id}`}
                        >
                          <ShieldOff className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleOpenEdit(studio)}
                          title="Edit studio"
                          data-testid={`edit-studio-${studio.slug || studio.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleToggleActive(studio.id, studio.is_active)}
                          disabled={togglingId === studio.id}
                          title={studio.is_active ? 'Deactivate studio' : 'Activate studio'}
                        >
                          {togglingId === studio.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : studio.is_active ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          asChild
                          title="View studio page"
                        >
                          <a href={`/studio/${studio.slug}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No studios found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Studio Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Studio</DialogTitle>
            <DialogDescription>
              Update all studio information. Changes will be applied immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-medium mb-3">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Studio Name *</Label>
                  <Input 
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Studio Name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL Slug *</Label>
                  <Input 
                    value={editForm.slug}
                    onChange={(e) => {
                      // Allow typing but only keep valid characters (a-z, 0-9, -)
                      const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                      setEditForm({ ...editForm, slug: value });
                    }}
                    placeholder="studio-name"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    URL: studioscheduler.ie/{editForm.slug || 'studio-name'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yoga">Yoga Studio</SelectItem>
                      <SelectItem value="pilates">Pilates Studio</SelectItem>
                      <SelectItem value="crossfit">CrossFit Box</SelectItem>
                      <SelectItem value="gym">Gym</SelectItem>
                      <SelectItem value="fitness">Fitness Center</SelectItem>
                      <SelectItem value="dance">Dance Studio</SelectItem>
                      <SelectItem value="martial_arts">Martial Arts</SelectItem>
                      <SelectItem value="swimming">Swimming Pool</SelectItem>
                      <SelectItem value="physiotherapy">Physiotherapy</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Company Type</Label>
                  <Select value={editForm.company_type} onValueChange={(v) => setEditForm({ ...editForm, company_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_trader">Sole Trader</SelectItem>
                      <SelectItem value="registered_company">Registered Company</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 mt-4">
                <Label>Description</Label>
                <Textarea 
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Studio description..."
                  rows={3}
                />
              </div>
            </div>

            <Separator />

            {/* Address Information */}
            <div>
              <h3 className="text-sm font-medium mb-3">Address</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Input 
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Town/City</Label>
                    <Input 
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      placeholder="Dublin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>County</Label>
                    <Select value={editForm.county || ''} onValueChange={(v) => setEditForm({ ...editForm, county: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select county" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="carlow">Carlow</SelectItem>
                        <SelectItem value="cavan">Cavan</SelectItem>
                        <SelectItem value="clare">Clare</SelectItem>
                        <SelectItem value="cork">Cork</SelectItem>
                        <SelectItem value="donegal">Donegal</SelectItem>
                        <SelectItem value="dublin">Dublin</SelectItem>
                        <SelectItem value="galway">Galway</SelectItem>
                        <SelectItem value="kerry">Kerry</SelectItem>
                        <SelectItem value="kildare">Kildare</SelectItem>
                        <SelectItem value="kilkenny">Kilkenny</SelectItem>
                        <SelectItem value="laois">Laois</SelectItem>
                        <SelectItem value="leitrim">Leitrim</SelectItem>
                        <SelectItem value="limerick">Limerick</SelectItem>
                        <SelectItem value="longford">Longford</SelectItem>
                        <SelectItem value="louth">Louth</SelectItem>
                        <SelectItem value="mayo">Mayo</SelectItem>
                        <SelectItem value="meath">Meath</SelectItem>
                        <SelectItem value="monaghan">Monaghan</SelectItem>
                        <SelectItem value="offaly">Offaly</SelectItem>
                        <SelectItem value="roscommon">Roscommon</SelectItem>
                        <SelectItem value="sligo">Sligo</SelectItem>
                        <SelectItem value="tipperary">Tipperary</SelectItem>
                        <SelectItem value="waterford">Waterford</SelectItem>
                        <SelectItem value="westmeath">Westmeath</SelectItem>
                        <SelectItem value="wexford">Wexford</SelectItem>
                        <SelectItem value="wicklow">Wicklow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Eircode</Label>
                    <Input 
                      value={editForm.eircode}
                      onChange={(e) => setEditForm({ ...editForm, eircode: e.target.value.toUpperCase() })}
                      placeholder="D01 AB12"
                      maxLength={8}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact Information */}
            <div>
              <h3 className="text-sm font-medium mb-3">Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="studio@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+353 1 234 5678"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Cancellation Policy */}
            <div>
              <h3 className="text-sm font-medium mb-3">Cancellation Policy</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Notice Hours</Label>
                  <Input 
                    type="number"
                    value={editForm.cancellation_notice_hours}
                    onChange={(e) => setEditForm({ ...editForm, cancellation_notice_hours: parseInt(e.target.value) || 24 })}
                    min={0}
                    placeholder="24"
                  />
                  <p className="text-xs text-muted-foreground">Hours before class</p>
                </div>
                <div className="space-y-2">
                  <Label>Refund Type</Label>
                  <Select value={editForm.cancellation_refund_type} onValueChange={(v) => setEditForm({ ...editForm, cancellation_refund_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="class_pass_credit">Credit to ClassPass</SelectItem>
                      <SelectItem value="full_refund">Full Refund</SelectItem>
                      <SelectItem value="no_refund">No Refund</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Late Cancel Penalty (€)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={editForm.late_cancel_penalty || ''}
                    onChange={(e) => setEditForm({ ...editForm, late_cancel_penalty: parseFloat(e.target.value) || 0 })}
                    min={0}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Platform Fee Override (Super-Admin only) */}
            <div>
              <h3 className="text-sm font-medium mb-3">Platform Fee Override</h3>
              <div className="space-y-2">
                <Label>Custom Fee % (optional)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={editForm.platform_fee_percentage_override}
                    onChange={(e) => setEditForm({ ...editForm, platform_fee_percentage_override: e.target.value })}
                    placeholder="e.g. 0.5"
                    className="max-w-xs"
                    data-testid="platform-fee-override-input"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  {editForm.platform_fee_percentage_override !== '' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditForm({ ...editForm, platform_fee_percentage_override: '' })}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Overrides this studio's platform fee. Leave empty to use the studio's subscription-plan rate
                  (or the platform's global default when there is no plan).
                </p>
              </div>
            </div>

            <Separator />

            {/* Status */}
            <div>
              <h3 className="text-sm font-medium mb-3">Status</h3>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Studio Active</Label>
                  <p className="text-xs text-muted-foreground">When disabled, studio will not appear in listings</p>
                </div>
                <Switch 
                  checked={editForm.is_active} 
                  onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })} 
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
